var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { v4 as uuid } from 'uuid';
import { constructArgumentCollection, deconstructArgumentCollection } from './arguments';
import { filterErrors } from './errors';
import { DominoBuilderExpression } from './expressions';
import { generateResultUniqueName } from './helpers';
import { constructFlowSymbols } from './symbols';
import { HandleType } from './types';
export const undefinedAction = {
    handle: undefined,
    scopeRef: 'global',
    lineNumber: -1,
};
export const constructActions = (callDefinitions, resourceRegistry, symbolTable) => __awaiter(void 0, void 0, void 0, function* () {
    // as of now, this method cannot understand sugary actions
    return Promise.all(callDefinitions.map(
    // line number will need to be a more sophisticated calculation once blocks become supported (i.e. branching, iteration, etc)
    (callDefinition, idx) => __awaiter(void 0, void 0, void 0, function* () { return yield constructAction(idx, callDefinition, resourceRegistry, symbolTable); }))).then(constructedActions => {
        // validate all expressions after actions have been constructed to ensure that all symbols have been created
        constructedActions.map(a => a.validateExpressions(a.scopeRef));
        return constructedActions;
    });
});
export const constructAction = (lineNumber, callDefinition, resourceRegistry, symbolTable, prevHandle = undefined) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c;
    // any syntactic sugar translations should happen here
    const errors = [];
    let actionDefinition;
    try {
        actionDefinition = (yield resourceRegistry.getResource(callDefinition.call));
    }
    catch (error) {
        errors.push(error);
    }
    const validateExpressions = (scopeRef) => {
        var _a;
        (_a = action.argsCollection) === null || _a === void 0 ? void 0 : _a.args.map(arg => {
            const effectiveScope = symbolTable.calculateEffectiveScope(scopeRef);
            validateExpressionComponents(arg.type, arg.value, effectiveScope, lineNumber);
        });
    };
    const resultName = (_a = callDefinition.result) !== null && _a !== void 0 ? _a : generateResultUniqueName(callDefinition.call);
    let action = {
        handle: prevHandle !== null && prevHandle !== void 0 ? prevHandle : { id: uuid(), type: HandleType.Action },
        type: callDefinition.call,
        result: resultName,
        scopeRef: symbolTable.currentScopeId,
        lineNumber,
        category: actionDefinition === null || actionDefinition === void 0 ? void 0 : actionDefinition.category,
        validateExpressions,
    };
    if (!!actionDefinition) {
        /**
         * If the action has defined results, we convert the shape of these results into an entity with the name of
         * result uniquename specified in the call definition. This result becomes a scoped symbol in the FlowSymbolTable.
         */
        if (actionDefinition.returnType) {
            const typeDefinition = {
                name: 'ActionResult',
                properties: actionDefinition.returnType.map(r => ({
                    name: r.name,
                    type: r.type,
                    label: r.label,
                    description: r.description,
                })),
                spec: {},
            };
            yield constructFlowSymbols(resultName, typeDefinition, resourceRegistry, symbolTable, action);
        }
        const argsCollection = yield constructArgumentCollection(resourceRegistry, actionDefinition.params, callDefinition.args, HandleType.ActionArgument, action.handle);
        action = Object.assign(Object.assign({}, action), { label: (_c = (_b = callDefinition.label) !== null && _b !== void 0 ? _b : actionDefinition.label) !== null && _c !== void 0 ? _c : actionDefinition.name, description: actionDefinition.description, alerts: actionDefinition.alerts, icon: actionDefinition.icon, paramGroups: actionDefinition.paramGroups, argsCollection });
    }
    action = Object.assign(Object.assign({}, action), { errors, getErrorAggregate: (scope, severity) => {
            var _a, _b, _c;
            return filterErrors(scope, severity, errors).concat((_c = (_b = (_a = action.argsCollection) === null || _a === void 0 ? void 0 : _a.getErrorAggregate) === null || _b === void 0 ? void 0 : _b.call(_a, scope, severity)) !== null && _c !== void 0 ? _c : []);
        } });
    return action;
});
export const validateExpressionComponents = (_type, value, scope, lineNumber) => {
    if (!value) {
        return;
    }
    if (typeof value === 'object') {
        // value will either be an object with properties that may be expressions OR it will be an expression (if no entity def found)
        if (value instanceof DominoBuilderExpression) {
            value === null || value === void 0 ? void 0 : value.construct(scope, lineNumber);
        }
        else {
            Object.keys(value).map((k) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                validateExpressionComponents((_a = value[k]) === null || _a === void 0 ? void 0 : _a._expectedType, value[k], scope, lineNumber);
            }));
        }
    }
    else if (Array.isArray(value)) {
        value.forEach(item => {
            validateExpressionComponents(null, item, scope, lineNumber);
        });
    }
};
export const deconstructActions = (actions) => {
    const referencedActionIds = [];
    const mappedActions = actions.map(a => deconstructAction(a, referencedActionIds));
    return mappedActions.map(([actionId, callDef]) => {
        if (referencedActionIds.find(referencedActionId => referencedActionId === actionId)) {
            return callDef;
        }
        else {
            // strip result property
            delete callDef.result;
            return callDef;
        }
    });
};
export const deconstructAction = (action, referencedActionIds) => {
    const args = deconstructArgumentCollection(action.argsCollection, referencedActionIds);
    const stripProperties = ({ result, type, label }) => {
        var _a;
        const strippedAction = { call: type };
        if (!!label) {
            strippedAction.label = label;
        }
        if (!!result) {
            strippedAction.result = result;
        }
        if (((_a = action.argsCollection) === null || _a === void 0 ? void 0 : _a.args.length) > 0) {
            strippedAction.args = args;
        }
        return strippedAction;
    };
    return [action.handle.id, stripProperties(action)];
};
//# sourceMappingURL=actions.js.map